// useBaseAccessLock.js

import { useEffect, useMemo, useState } from 'react';

import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

import {
  getBaseLockReason,
  subscribeToBaseBillingRecord,
} from 'services/billing/BaseBillingService';
import { getPlanByKey } from 'services/plans/PlanService';

/**
 * Resolves unified base access lock state for the active base.
 */
const useBaseAccessLock = () => {
  const { isEmailVerified, isSuperUser, user } = useUser();
  const { activeBase } = useBase();
  const [billingRecord, setBillingRecord] = useState(null);
  const [activePlan, setActivePlan] = useState(null);

  const baseKey = String(activeBase?.key || '').trim();
  const planKey = String(activeBase?.planKey || activeBase?.planId || '').trim();

  useEffect(() => {
    if (!baseKey || !user?.key) {
      setBillingRecord(null);
      return undefined;
    }

    return subscribeToBaseBillingRecord(baseKey, setBillingRecord);
  }, [user?.key, baseKey]);

  useEffect(() => {
    if (activeBase?.planBypassesTrialLock === true) {
      setActivePlan(null);
      return undefined;
    }

    if (!planKey) {
      setActivePlan(null);
      return undefined;
    }

    let isDisposed = false;

    getPlanByKey({ planKey })
      .then((plan) => {
        if (!isDisposed) {
          setActivePlan(plan || null);
        }
      })
      .catch(() => {
        if (!isDisposed) {
          setActivePlan(null);
        }
      });

    return () => {
      isDisposed = true;
    };
  }, [activeBase?.planBypassesTrialLock, planKey]);

  const lockReason = useMemo(() => {
    if (!user?.key || !isEmailVerified || isSuperUser || !activeBase?.key) {
      return null;
    }

    return getBaseLockReason(activeBase, billingRecord, new Date(), activePlan);
  }, [activePlan, activeBase, billingRecord, isEmailVerified, isSuperUser, user?.key]);

  return {
    accessLocked: Boolean(lockReason),
    activePlan,
    billingRecord,
    lockReason,
  };
};

export default useBaseAccessLock;
